import React, { useState, useEffect } from 'react';
import { shiftsAPI, usersAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Clock, Plus, Sun, Moon, Sunrise } from 'lucide-react';

const SHIFT_TYPES = { morning: { icon: Sunrise, color: '#B89B5E' }, day: { icon: Sun, color: '#1E5A8A' }, evening: { icon: Moon, color: '#4A1D2A' }, night: { icon: Moon, color: '#0F1115' } };

export default function AdminShifts() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [form, setForm] = useState({ user_id: '', date: selectedDate, start_time: '09:00', end_time: '17:00', shift_type: 'day', desk: '', notes: '' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadUsers(); }, [selectedDate]);

  const load = async () => {
    try {
      const res = await shiftsAPI.list({ date: selectedDate });
      setShifts(res.data.shifts || []);
    } catch { toast.error('Failed to load shifts'); }
    finally { setLoading(false); }
  };

  const loadUsers = async () => {
    try {
      const res = await usersAPI.list({ limit: 100 });
      setUsers(res.data.users || []);
    } catch {}
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await shiftsAPI.create(form);
      toast.success('Shift created');
      setShowCreate(false);
      load();
    } catch { toast.error('Failed to create shift'); }
  };

  const deleteShift = async (id) => {
    if (!window.confirm('Delete this shift?')) return;
    try {
      await shiftsAPI.delete(id);
      toast.success('Shift deleted');
      load();
    } catch { toast.error('Failed to delete shift'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Shift Scheduling</h1>
          <p className="text-sm text-[#5B616E] mt-1">Manage newsroom shift rotas and desk assignments</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#1E5A8A] hover:bg-[#1E5A8A]/90">
          <Plus className="w-4 h-4 mr-2" /> Add Shift
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <label className="text-sm font-medium text-[#5B616E]">Date:</label>
        <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" />
        <span className="text-sm text-[#5B616E]">{shifts.length} shifts scheduled</span>
      </div>

      {showCreate && (
        <div className="bg-white rounded-xl border border-[#E5DFD5] p-6">
          <h2 className="text-lg font-semibold mb-4">Create Shift</h2>
          <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Staff Member</label>
              <select value={form.user_id} onChange={e => setForm({ ...form, user_id: e.target.value })} required className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm">
                <option value="">Select staff...</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Date</label>
              <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Shift Type</label>
              <select value={form.shift_type} onChange={e => setForm({ ...form, shift_type: e.target.value })} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm">
                <option value="morning">Morning</option>
                <option value="day">Day</option>
                <option value="evening">Evening</option>
                <option value="night">Night</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Start Time</label>
              <input type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#5B616E]">End Time</label>
              <input type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Desk</label>
              <input value={form.desk} onChange={e => setForm({ ...form, desk: e.target.value })} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" placeholder="News Desk A" />
            </div>
            <div className="md:col-span-3 flex gap-2">
              <Button type="submit" className="bg-[#1E5A8A]">Create Shift</Button>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full text-center py-8 text-[#5B616E]">Loading shifts...</div>
        ) : shifts.length === 0 ? (
          <div className="col-span-full text-center py-8 text-[#5B616E]">No shifts scheduled for this date.</div>
        ) : shifts.map(s => {
          const shiftType = SHIFT_TYPES[s.shift_type] || SHIFT_TYPES.day;
          const Icon = shiftType.icon;
          return (
            <div key={s.id} className="bg-white rounded-xl border border-[#E5DFD5] p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: shiftType.color + '15', color: shiftType.color }}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1A1A1A]">{s.user_name || 'Unassigned'}</p>
                    <p className="text-xs text-[#5B616E] capitalize">{s.shift_type} shift</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7 px-2" onClick={() => deleteShift(s.id)}>×</Button>
              </div>
              <div className="flex items-center gap-3 text-sm text-[#5B616E]">
                <Clock className="w-4 h-4" />
                <span>{s.start_time} – {s.end_time}</span>
              </div>
              {s.desk && <p className="text-xs text-[#5B616E] mt-2">Desk: {s.desk}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
